'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { track } from '@/utils/track';
import { setupLcpTracking, setupPageStatusReport, setupSlowReportTracking, getNetworkInfo } from '@/utils/performance-track';

/**
 * 性能追踪组件
 * - 初次挂载：启动 LCP 追踪、Web Vitals 采集、拦截 history.pushState
 * - 路由切换后：测量从 pushState 发起到新页面渲染完成的耗时，上报 page_navigation_timing
 */
export default function PerformanceTracker() {
  const pathname = usePathname();
  const isFirstMount = useRef(true);

  useEffect(() => {
    setupLcpTracking();
    setupPageStatusReport();
    setupSlowReportTracking();

    // 拦截 history.pushState，记录路由切换发起时刻、来源路由和导航类型
    const orig = history.pushState.bind(history);
    history.pushState = function (...args: Parameters<typeof history.pushState>) {
      (window as any).__routeChangeStart = performance.now();
      (window as any).__routeFromPath = window.location.pathname;
      (window as any).__routeNavType = 'push';
      return orig(...args);
    };

    const handlePopState = () => {
      (window as any).__routeChangeStart = performance.now();
      (window as any).__routeFromPath = window.location.pathname;
      (window as any).__routeNavType = 'pop';
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      history.pushState = orig;
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    // 跳过初次挂载（硬导航，不计入路由切换）
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    const start = (window as any).__routeChangeStart as number | undefined;
    if (!start) return;

    // 双 RAF 确保等待浏览器 paint 完成
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const renderMs = Math.round(performance.now() - start);
        const fromPath = (window as any).__routeFromPath as string | undefined;
        const navType = (window as any).__routeNavType as string | undefined;
        (window as any).__routeChangeStart = undefined;
        (window as any).__routeFromPath = undefined;
        (window as any).__routeNavType = undefined;
        track('page_navigation_timing', {
          from_path: fromPath ?? '',
          to_path: pathname,
          navigation_type: navType ?? 'push',
          render_ms: renderMs,
          network_effective_type: getNetworkInfo().network_effective_type,
        });
      });
    });
  }, [pathname]);

  return null;
}
